import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid3X3, List, Eye, Heart, MessageCircle, Share2, Edit3 } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const ArtistArtworksPage = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const itemsPerPage = 12;

  useEffect(() => {
    loadArtworks();
  }, [currentPage, searchQuery, sortBy]);

  const loadArtworks = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found');
        return;
      }

      console.log('Loading artworks for user:', user.id);
      console.log('User email:', user.email);

      // First, let's check what's in the vcs table for this user
      const { data: allUserArtworks, error: checkError } = await supabase
        .from('vcs')
        .select('*')
        .eq('uuid', user.id);

      console.log('All user artworks (no filters):', allUserArtworks);
      console.log('Check error:', checkError);

      if (checkError) {
        console.error('Error checking user artworks:', checkError);
      }

      let query = supabase
        .from('vcs')
        .select('vc_id, title, cover_image, price, created_at, status, mode', { count: 'exact' })
        .eq('uuid', user.id)
        .eq('access_control', 'Public');

      // Handle sorting
      if (sortBy === 'price_asc') {
        query = query.order('price', { ascending: true });
      } else if (sortBy === 'price') {
        query = query.order('price', { ascending: false });
      } else {
        query = query.order(sortBy, { ascending: false });
      }

      // Add search filter
      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      // Add pagination
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      console.log('Final query results:', { data, error, count });
      console.log('Query filters applied:', { 
        artist_uuid: user.id, 
        access_control: 'Public',
        sortBy,
        searchQuery,
        pagination: { from, to }
      });

      if (error) {
        console.error('Error loading artworks:', error);
        return;
      }

      if (currentPage === 1) {
        setArtworks(data || []);
      } else {
        setArtworks(prev => [...prev, ...(data || [])]);
      }

      setTotalCount(count || 0);
      setHasMore((data || []).length === itemsPerPage);
    } catch (error) {
      console.error('Error loading artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setArtworks([]);
  };

  const handleLoadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    setArtworks([]);
  };

  const handleViewModeToggle = () => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    return `$${parseFloat(price).toLocaleString()}`;
  };



  return (
    <div className="artist-artworks-page">
      {/* Page Header */}
      <div className="artworks-page-header">
        <div className="artworks-header-content">
          <div className="artworks-header-left">
            <h1 className="artworks-page-title">My Artworks</h1>
            <p className="artworks-page-subtitle">
              {totalCount} artwork{totalCount !== 1 ? 's' : ''} in your portfolio
            </p>
          </div>
          
          <div className="artworks-header-actions">
            <button
              onClick={handleViewModeToggle}
              className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
              title={viewMode === 'grid' ? 'Switch to List View' : 'Switch to Grid View'}
            >
              {viewMode === 'grid' ? (
                <>
                  <List size={16} color="currentColor" />
                  <span className="btn-text">List View</span>
                </>
              ) : (
                <>
                  <Grid3X3 size={16} color="currentColor" />
                  <span className="btn-text">Grid View</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="artworks-controls">
        <form onSubmit={handleSearch} className="artworks-search-form">
                  <div className="search-wrapper">
          <Search className="search-icon" color="#6c757d" />
          <input
              type="text"
              placeholder="Search artworks by title, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="sort-select"
            >
              <option value="created_at">Newest First</option>
              <option value="title">Title A-Z</option>
              <option value="price">Price High to Low</option>
              <option value="price_asc">Price Low to High</option>
            </select>
          </div>
          
          <button type="submit" className="search-btn">
            Search
          </button>
        </form>
      </div>

      {/* Artworks Grid/List */}
      <div className="artworks-container">
        {loading && artworks.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your artworks...</p>
          </div>
        ) : artworks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎨</div>
            <h3 className="empty-title">No artworks found</h3>
            <p className="empty-description">
              {searchQuery ? 'Try adjusting your search terms.' : 'Start by uploading your first artwork!'}
            </p>
            {!searchQuery && (
              <Link to="/artist/upload" className="empty-action">
                Upload Artwork
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className={`artworks-grid ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
              {artworks.map((artwork) => (
                <div key={artwork.vc_id} className="artwork-card">
                  <div className="artwork-image-container">
                    <img
                      src={artwork.cover_image || '/images/artwork-placeholder.jpg'}
                      alt={artwork.title || 'Untitled Artwork'}
                      className="artwork-image"
                      onError={(e) => {
                        e.target.src = '/images/artwork-placeholder.jpg';
                      }}
                    />
                    
                    {/* Status badge for list view */}
                    {viewMode === 'list' && artwork.status && (
                      <div className={`status-badge ${artwork.status.toLowerCase()}`}>
                        {artwork.status}
                      </div>
                    )}
                    
                    <div className="artwork-overlay">
                      <div className="artwork-actions">
                        <Link 
                          to={`/artist/artwork/${artwork.vc_id}`}
                          className="artwork-action-btn"
                          title="View Details"
                        >
                          <Eye size={16} color="#6c757d" />
                        </Link>
                        <button className="artwork-action-btn" title="Edit">
                          <Edit3 size={16} color="#6c757d" />
                        </button>
                        <button className="artwork-action-btn" title="Share">
                          <Share2 size={16} color="#6c757d" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="artwork-info">
                    <h3 className="artwork-title">{artwork.title || 'Untitled'}</h3>
                    
                    <div className="artwork-meta">
                      {artwork.status && (
                        <span className={`status-tag ${artwork.status.toLowerCase()}`}>
                          {artwork.status}
                        </span>
                      )}
                      <span className="artwork-price">{formatPrice(artwork.price)}</span>
                      {artwork.mode && (
                        <span className="artwork-mode">{artwork.mode}</span>
                      )}
                    </div>
                    
                    <div className="artwork-footer">
                      <div className="artwork-actions-row">
                        <Link to={`/artist/artwork/${artwork.vc_id}`} className="view-details-link">
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="load-more-container">
                <button
                  onClick={handleLoadMore}
                  className="load-more-btn"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More Artworks'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ArtistArtworksPage;
